import React, { useState, useEffect, useCallback } from 'react';
import {
    Search,
    RefreshCw,
    CheckCircle,
    XCircle,
    Clock,
    Mail,
    AlertCircle,
    Loader2,
    RotateCw
} from 'lucide-react';
import { apiClient, NotificationLog } from '../services/api';

interface NotificationLogsProps {
    user: any;
}

const NotificationLogs: React.FC<NotificationLogsProps> = ({ user }) => {
    const [logs, setLogs] = useState<NotificationLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryingId, setRetryingId] = useState<string | null>(null);

    // Filter states
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 20;

    const loadLogs = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await apiClient.getNotificationLogs({
                page: currentPage,
                limit: pageSize,
                type: typeFilter !== 'all' ? typeFilter : undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
            });

            setLogs(response.data);
            setTotalPages(response.pagination.totalPages);
            setTotalCount(response.pagination.total);
        } catch (err: any) {
            setError(err.message || 'Failed to load notification logs');
            console.error('Error loading logs:', err);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, typeFilter, statusFilter]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    const handleRetry = async (logId: string) => {
        try {
            setRetryingId(logId);
            await apiClient.retryNotification(logId);
            await loadLogs();
        } catch (err: any) {
            setError(err.message || 'Failed to retry notification');
        } finally {
            setRetryingId(null);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'delivered':
                return {
                    icon: CheckCircle,
                    color: 'text-green-800',
                    bgColor: 'bg-green-100',
                    label: 'Delivered'
                };
            case 'sent':
                return {
                    icon: Mail,
                    color: 'text-blue-800',
                    bgColor: 'bg-blue-100',
                    label: 'Sent'
                };
            case 'pending':
                return {
                    icon: Clock,
                    color: 'text-yellow-800',
                    bgColor: 'bg-yellow-100',
                    label: 'Pending'
                };
            case 'failed':
                return {
                    icon: XCircle,
                    color: 'text-red-800',
                    bgColor: 'bg-red-100',
                    label: 'Failed'
                };
            case 'bounced':
                return {
                    icon: AlertCircle,
                    color: 'text-orange-800',
                    bgColor: 'bg-orange-100',
                    label: 'Bounced'
                };
            default:
                return {
                    icon: Clock,
                    color: 'text-gray-800',
                    bgColor: 'bg-gray-100',
                    label: status
                };
        }
    };

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'licence_expiry': return 'Licence Expiry';
            case 'consent_expiry': return 'Consent Expiry';
            case 'compliance_alert': return 'Compliance Alert';
            case 'bulk_operation': return 'Bulk Operation';
            case 'daily_digest': return 'Daily Digest';
            default: return type;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredLogs = searchTerm
        ? logs.filter(log =>
            log.recipient.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.subject.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : logs;

    return (
        <div className="space-y-6">
            {/* Filters and Search */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div className="md:col-span-2 relative">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by recipient or subject..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                    </div>

                    {/* Type Filter */}
                    <div>
                        <select
                            value={typeFilter}
                            onChange={(e) => {
                                setTypeFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            <option value="all">All Types</option>
                            <option value="licence_expiry">Licence Expiry</option>
                            <option value="consent_expiry">Consent Expiry</option>
                            <option value="compliance_alert">Compliance Alert</option>
                            <option value="bulk_operation">Bulk Operation</option>
                            <option value="daily_digest">Daily Digest</option>
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            <option value="all">All Status</option>
                            <option value="delivered">Delivered</option>
                            <option value="sent">Sent</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                            <option value="bounced">Bounced</option>
                        </select>
                    </div>
                </div>

                {/* Summary and Refresh */}
                <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                        Showing {filteredLogs.length} of {totalCount} notifications
                    </p>
                    <button
                        onClick={loadLogs}
                        disabled={isLoading}
                        className="inline-flex items-center px-3 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                </div>
            )}

            {/* Logs Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {isLoading ? (
                    <div className="p-8 text-center">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: '#69BE28' }} />
                        <p className="mt-2 text-gray-600">Loading notification history...</p>
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="p-8 text-center">
                        <Mail className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Notifications Found</h3>
                        <p className="text-gray-500">
                            {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
                                ? 'Try adjusting your filters.'
                                : 'Notifications will appear here once configured and sent.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Recipient
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Subject
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Sent At
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredLogs.map((log) => {
                                    const statusBadge = getStatusBadge(log.status);
                                    const StatusIcon = statusBadge.icon;

                                    return (
                                        <tr key={log.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {getTypeLabel(log.notificationType)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                                                    <span className="text-sm text-gray-900">{log.recipient}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-900">{log.subject}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.bgColor} ${statusBadge.color}`}>
                                                    <StatusIcon className="w-3 h-3 mr-1" />
                                                    {statusBadge.label}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {log.sentAt ? formatDate(log.sentAt) : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {(log.status === 'failed' || log.status === 'bounced') && (
                                                    <button
                                                        onClick={() => handleRetry(log.id)}
                                                        disabled={retryingId === log.id}
                                                        className="inline-flex items-center text-sm font-medium hover:opacity-80 disabled:opacity-50"
                                                        style={{ color: '#69BE28' }}
                                                    >
                                                        {retryingId === log.id ? (
                                                            <>
                                                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                                Retrying...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <RotateCw className="w-4 h-4 mr-1" />
                                                                Retry
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationLogs;